import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useStaffMayRefer } from "hooks/useReferralPolicy";
import axios from "axios";
import InputField from "components/Input/InputField";
import WholeScreenLoader from "components/WholeScreenLoader";
import { maritalStatusOptions } from "const/constants";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { convertDateFormat } from "components/helper";
import ClientMultiStepForm from "./ClientMultiStepForm";
import { getUserType, hasPermissionKey } from "utils/permissionUtils";
import { P } from "const/permissions";
import {
  buildReferralOptions,
  fetchAssignableReferralPartners,
  referralRequestFromSelection,
} from "./referralPicker";
import { canAssignReferrer, canChangeReferrer } from "./referrerAccess";
import { referrerFieldsForSave } from "./referrerFormPayload";
import ReferrerChangeDialog from "./ReferrerChangeDialog";
import CaseOfficerHistoryDialog from "./CaseOfficerHistoryDialog";
import { caseOfficerFields, caseOfficerOption } from "./caseOfficerOption";

const AddClient = ({ importedData, setOpenEditModal, setEditData }) => {
  const currentUserType = getUserType();
  const isUserRole = currentUserType === "USER";
  const { id } = useParams();
  const referralFilter = createFilterOptions();

  // If user role and not editing, show multi-step form

  const [data, setData] = useState({
    name: null,
    address: null,
    address2: null,
    country: null,
    state: null,
    city: null,
    zipCode: null,
    email: null,
    image: null,
    gender: null,
    usiNumber: null,
    emergencyContactName: null,
    emergencyContact: null,
    emergencyRelation: null,
    emergencyEmail: null,
    maritalStatus: null,
    active: true,
    isConverted: false,
    visaLodged: false,
    customReferral: false,
    bridgingVisa: null,
  });

  const [{ usersList, pagination }, { refetchPaginatedClient, refetchClient }] = useKothar();

  const [formSearchQuery, setFormSearchQuery] = useState("");
  const [formSearchResults, setFormSearchResults] = useState([]);
  const [formSearchLoading, setFormSearchLoading] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const searchDebounceRef = useRef(null);

  const navigate = useNavigate();
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({ ...prevState, [name]: value }));
  };

  const userDetails = JSON.parse(localStorage.getItem("userDetail"));

  const { state } = useLocation();
  const orgUsers = usersList?.filter((user) => user?.organization?.id === userDetails?.orgId);

  // Partners for the referral picker, fresh on every mount of this page.
  const { data: activePartners = [] } = useQuery(
    ["referral-partners-assignable"],
    fetchAssignableReferralPartners,
    { refetchOnMount: true, staleTime: 60_000 }
  );

  const staffMayRefer = useStaffMayRefer();
  const referralOptions = useMemo(
    () =>
      buildReferralOptions({
        partners: activePartners,
        users: orgUsers || [],
        includeStaff: staffMayRefer,
      }),
    [activePartners, orgUsers, staffMayRefer]
  );

  // On an existing client the referrer changes only through the dialog; the form resends what it
  // loaded (see referrerFieldsForSave).
  const canAssign = canAssignReferrer();
  const canAssignOfficer = hasPermissionKey(P.CLIENTS.ASSIGN_OFFICER);
  const isExistingClient = Boolean(data?.id);
  const [loadedClient, setLoadedClient] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [officerHistoryOpen, setOfficerHistoryOpen] = useState(false);

  const selectedReferralOption = useMemo(() => {
    if (data?.referralPartnerId) {
      return (
        referralOptions.find(
          (option) => option.kind === "PARTNER" && option.partnerId === data.referralPartnerId
        ) || null
      );
    }
    return (
      referralOptions.find(
        (option) =>
          option.kind === "STAFF" &&
          (option.email === data?.referral || option.label === data?.referral)
      ) ||
      (data?.customReferral && data?.referral
        ? { label: data.referral, inputValue: data.referral }
        : null)
    );
  }, [referralOptions, data?.referralPartnerId, data?.referral, data?.customReferral]);

  const applyReferralSelection = (value) => {
    const request = referralRequestFromSelection(
      typeof value === "string" ? { inputValue: value } : value
    );
    setData((prevState) => ({
      ...prevState,
      referralPartnerId: request.referralPartnerId,
      referralUserId: request.referralUserId,
      referral: request.referral,
      customReferral: request.customReferral,
    }));
  };

  const { mutate: getClientData, isLoading: isClientLoading } = useMutation({
    mutationFn: () => {
      return axios.get(`/clients/${id}`);
    },
    enabled: !!id,
    onSuccess: (response) => {
      const responseData = response?.data?.data;
      setLoadedClient(responseData);
      setData({
        ...responseData,
        dob: convertDateFormat(responseData?.dob),
        dateOfIssue: convertDateFormat(responseData?.dateOfIssue),
        visaExpiry: convertDateFormat(responseData?.visaExpiry),
        passportExpiry: convertDateFormat(responseData?.passportExpiry),
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    if (id && !importedData) {
      getClientData(id);
    }
  }, [id]);

  useEffect(() => {
    if (importedData) {
      setLoadedClient(importedData);
      setData({
        ...importedData,
        id: importedData?.clientId ?? importedData?.id,
        // The imported record is the client itself, so it wins over whatever partial
        // emergency block the host screen was navigated with.
        emergencyContactName: importedData?.emergencyContactName || state?.item?.emergency?.name,
        emergencyContact: importedData?.emergencyContact || state?.item?.emergency?.contact,
        emergencyRelation: importedData?.emergencyRelation || state?.item?.emergency?.relation,
        emergencyEmail: importedData?.emergencyEmail || state?.item?.emergency?.email,
      });
    }
  }, [importedData]);

  useEffect(() => {
    if (!formSearchQuery.trim()) {
      setFormSearchResults([]);
      return;
    }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setFormSearchLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/client-form/search?q=${encodeURIComponent(formSearchQuery)}`
        );
        setFormSearchResults(res?.data?.data ?? []);
      } catch {
        setFormSearchResults([]);
      } finally {
        setFormSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [formSearchQuery]);

  const handleImportFromForm = (formData) => {
    if (!formData) return;
    setSelectedFormId(formData.id);
    setData((prev) => ({
      ...prev,
      name: formData.name ?? prev.name,
      email: formData.email ?? prev.email,
      number: formData.number ?? prev.number,
      address: formData.australianAddress || formData.address || prev.address,
      dob: formData.dob ?? prev.dob,
      gender: formData.gender ?? prev.gender,
      maritalStatus: formData.maritalStatus ?? prev.maritalStatus,
      passportNumber: formData.passportNumber ?? prev.passportNumber,
      dateOfIssue: formData.dateOfIssue ?? prev.dateOfIssue,
      passportExpiry: formData.passportExpiry ?? prev.passportExpiry,
      visaStatus: formData.visaStatus ?? prev.visaStatus,
      visaExpiry: formData.visaExpiry ?? prev.visaExpiry,
      nationality: formData.nationality ?? prev.nationality,
      usiNumber: formData.usiNumber ?? prev.usiNumber,
      emergencyContactName: formData.emergencyContactName ?? prev.emergencyContactName,
      emergencyContact: formData.emergencyContact ?? prev.emergencyContact,
      emergencyRelation: formData.emergencyRelation ?? prev.emergencyRelation,
      emergencyEmail: formData.emergencyEmail ?? prev.emergencyEmail,
      onshoreAddress: formData.australianAddress ?? prev.onshoreAddress,
      homeAddress: formData.homeCountryAddress ?? prev.homeAddress,
      currentCourseProvider: formData.currentCourseProvider ?? prev.currentCourseProvider,
      intake: formData.intake ?? prev.intake,
    }));
  };

  const { mutate, isPending: isLoading } = useMutation(postData, {
    onSuccess(response) {
      const newClientId = response?.data?.data?.id;
      if (selectedFormId && newClientId) {
        axios
          .patch(`${API_URL}/client-form/${selectedFormId}/mark-converted?clientId=${newClientId}`)
          .catch((err) =>
            console.warn("mark-converted failed:", err?.response?.data?.message ?? err?.message)
          );
      }
      toast.success(data?.id ? "Data updated Successfully !!" : "Data added Successfully");
      if (importedData) {
        setOpenEditModal(false);
        setEditData(data);
      } else {
        navigate("/admin/client");
      }
      refetchClient();
      // refetchClient({
      //   pageIndex: 0,
      //   pageSize: 20,
      // });
      // Refetch with current pagination state and filters
      if (pagination) {
        refetchPaginatedClient({
          ...pagination,
          page: pagination.pageIndex,
          size: pagination.pageSize,
          searchText: pagination.searchText,
          caseOfficer: pagination.caseOfficer,
          createdMonth: pagination.createdMonth,
          createdYear: pagination.createdYear,
        });
      } else {
        // Fallback to default pagination if no state exists

        refetchPaginatedClient({
          ...pagination,
          page: pagination.pageIndex,
          size: pagination.pageSize,
          searchText: pagination.searchText,
          caseOfficer: pagination.caseOfficer,
          createdMonth: pagination.createdMonth,
          createdYear: pagination.createdYear,
        });
      }
      // refetchAccountList();
    },
    onError(error) {
      toast.error(error?.response?.data?.message ?? "Error Submitting Data");
    },
  });

  async function postData(payload) {
    if (data?.id) {
      return axios.put(`${API_URL}/clients/${payload?.id}`, payload);
    } else {
      return axios.post(`${API_URL}/clients`, payload);
    }
  }
  const selectedOfficer = caseOfficerOption(usersList, data);
  const unlinkedOfficerName =
    !selectedOfficer && data?.caseOfficer?.trim() ? data.caseOfficer.trim() : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      name: data?.name,
      address: data?.address,
      address2: data?.address2,
      country: data?.country,
      state: data?.state,
      city: data?.city,
      zipCode: data?.zipCode,
      email: data?.email,
      image: null,
      dob: data?.dob,
      number: data?.number,
      officePhoneCountryCode: data?.officePhoneCountryCode,
      officePhoneAreaCode: data?.officePhoneAreaCode,
      officePhoneNumber: data?.officePhoneNumber,
      gender: data?.gender,
      passportNumber: data?.passportNumber,
      dateOfIssue: data?.dateOfIssue,
      passportExpiry: data?.passportExpiry,
      visaStatus: data?.visaStatus,
      visaExpiry: data?.visaExpiry,
      nationality: null,
      status: null,
      active: data?.active ? "Y" : "N",
      usiNumber: data?.usiNumber,
      note: "uploaded from sheets data",
      maritalStatus: data?.maritalStatus,
      emergencyContactName: data?.emergencyContactName,
      emergencyContact: data?.emergencyContact,
      emergencyRelation: data?.emergencyRelation,
      emergencyEmail: data?.emergencyEmail,
      onshoreAddress: data?.onshoreAddress,
      homeAddress: data?.homeAddress,
      intake: data?.intake,
      currentCourseProvider: data?.currentCourseProvider,
      courseName: data?.courseName,
      campusLocation: data?.campusLocation,
      credits: data?.credits,
      caseOfficer: data?.caseOfficer,
      caseOfficerId: data?.caseOfficerId ?? null,
      ...referrerFieldsForSave({
        isNew: !data?.id,
        canAssign,
        data,
        original: loadedClient,
      }),
      testInfo: data?.testInfo,
      id: data?.id,
      clientId: data?.clientId,
      isConverted: data?.isConverted,
      visaLodged: data?.visaLodged,
      bridgingVisa: data?.bridgingVisa,
    };

    mutate({ ...payload });
  };

  if (isUserRole && !id && !importedData) {
    return <ClientMultiStepForm refetchClients={refetchPaginatedClient} />;
  }

  const sectionClass = "rounded-xl border border-app-border bg-app-surface p-5 shadow-sm";
  const sectionTitleClass = "text-base font-semibold text-app-text mb-4";
  const fieldWrapperClass = "relative w-full";

  return (
    <div className={importedData ? "flex flex-wrap mt-4" : "flex flex-wrap mt-4 dashBody"}>
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          {!importedData && (
            <div className="rounded-t mb-0 px-6 py-4 border-b border-app-border">
              <div className="w-full flex items-center justify-between">
                <div className="relative max-w-full flex justify-start gap-4 items-center">
                  <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                  <h3 className={"font-semibold text-xl text-app-text"}>
                    {id ? "Edit Client" : "Add Client"}
                  </h3>
                </div>

                <div className="flex items-center gap-2">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!data?.visaLodged}
                        onChange={(e) =>
                          setData((prev) => ({ ...prev, visaLodged: e.target.checked }))
                        }
                      />
                    }
                    label="Visa Lodged"
                  />{" "}
                  <FormControlLabel
                    control={
                      <Switch
                        sx={{ m: 1 }}
                        onChange={(e) => {
                          setData({ ...data, isConverted: e.target.checked });
                        }}
                        checked={data?.isConverted}
                      />
                    }
                    label="Converted"
                  />
                </div>
              </div>
            </div>
          )}
          <div className={`block w-full overflow-x-auto ${importedData ? "mt-0" : "mt-4"}`}>
            <div className="flex-auto lg:px-8 py-6 pt-2">
              <form className="space-y-5" onSubmit={handleSubmit}>
                {!id && !importedData && (
                  <div className={sectionClass}>
                    <h4 className={sectionTitleClass}>Import from Form Submission</h4>
                    <Autocomplete
                      options={formSearchResults}
                      loading={formSearchLoading}
                      inputValue={formSearchQuery}
                      onInputChange={(_, value) => setFormSearchQuery(value)}
                      onChange={(_, value) => handleImportFromForm(value)}
                      getOptionLabel={(option) =>
                        [option.name, option.passportNumber].filter(Boolean).join(" — ")
                      }
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      noOptionsText={
                        formSearchQuery.trim()
                          ? "No form submissions found"
                          : "Type to search by name, email, or passport number"
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{option.name}</span>
                            <span className="text-xs text-app-text-muted">
                              {[option.email, option.passportNumber, option.number]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </div>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search form submissions (name, email, passport number)"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {formSearchLoading && <CircularProgress size={16} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {selectedFormId && (
                      <p className="mt-2 text-xs text-green-700 font-medium">
                        Form data imported. Review and fill any missing fields before saving.
                      </p>
                    )}
                  </div>
                )}
                <div className={sectionClass}>
                  <h4 className={sectionTitleClass}>Personal Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-end">
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Name"
                        placeholder="Enter Full Name"
                        name="name"
                        required
                        type="text"
                        value={data?.name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Email Address"
                        type="email"
                        placeholder="Email Address"
                        name="email"
                        required
                        value={data?.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Mobile Number"
                        placeholder="Mobile Number"
                        name="number"
                        required
                        // type="number"
                        value={data?.number}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* Office-hours landline, in the three boxes government forms print it in.
                        Optional: most clients only ever give a mobile. */}
                    {/* <div className={fieldWrapperClass}>
                      <InputField
                        label="Office Phone Country Code"
                        placeholder="e.g. 61"
                        name="officePhoneCountryCode"
                        value={data?.officePhoneCountryCode}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Office Phone Area Code"
                        placeholder="e.g. 03"
                        name="officePhoneAreaCode"
                        value={data?.officePhoneAreaCode}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Office Phone Number"
                        placeholder="Office hours number"
                        name="officePhoneNumber"
                        value={data?.officePhoneNumber}
                        onChange={handleInputChange}
                      />
                    </div> */}
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Full Address"
                        placeholder="Enter Full Address"
                        name="address"
                        required
                        type="text"
                        value={data?.address}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/*
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Address 2"
                        placeholder="Apartment, suite, unit, etc."
                        name="address2"
                        type="text"
                        value={data?.address2}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Country"
                        placeholder="Country"
                        name="country"
                        type="text"
                        value={data?.country}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="State"
                        placeholder="State"
                        name="state"
                        type="text"
                        value={data?.state}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="City"
                        placeholder="City"
                        name="city"
                        type="text"
                        value={data?.city}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Zip Code"
                        placeholder="Zip Code"
                        name="zipCode"
                        type="text"
                        value={data?.zipCode}
                        onChange={handleInputChange}
                      />
                    </div>
                    */}
                    <div className={fieldWrapperClass}>
                      <InputField
                        type="date"
                        placeholder="Date of Birth"
                        name="dob"
                        label="Date of Birth"
                        value={data?.dob}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <label className="input-label">Select Marital Status *</label>
                      <Autocomplete
                        disablePortal
                        size="small"
                        options={maritalStatusOptions}
                        renderInput={(params) => <TextField {...params} label="Marital Status" />}
                        onChange={(e, value) => {
                          setData((prevState) => ({
                            ...prevState,
                            maritalStatus: value?.value,
                          }));
                        }}
                        value={
                          maritalStatusOptions?.find(
                            (item) => item?.value === data?.maritalStatus
                          ) ?? null
                        }
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <FormControl>
                        <FormLabel className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                          Gender
                        </FormLabel>
                        <RadioGroup
                          row
                          name="gender"
                          onChange={handleInputChange}
                          value={data?.gender}
                        >
                          <FormControlLabel value="Male" control={<Radio />} label="Male" />
                          <FormControlLabel value="Female" control={<Radio />} label="Female" />
                          <FormControlLabel value="other" control={<Radio />} label="Other" />
                        </RadioGroup>
                      </FormControl>
                    </div>
                  </div>
                </div>

                <div className={sectionClass}>
                  <h4 className={sectionTitleClass}>Visa & Passport</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-end">
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Passport Number"
                        placeholder="Passport Number"
                        name="passportNumber"
                        value={data?.passportNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Date of Issue"
                        name="dateOfIssue"
                        type="date"
                        value={data?.dateOfIssue || new Date(data?.dateOfIssue)}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Passport Expiry"
                        name="passportExpiry"
                        type="date"
                        value={data?.passportExpiry}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Visa Type"
                        name="visaStatus"
                        placeholder="Enter Visa Subclass Type"
                        type="text"
                        value={data?.visaStatus}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="Visa Expiry"
                        name="visaExpiry"
                        type="date"
                        value={data?.visaExpiry}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        label="USI Number"
                        name="usiNumber"
                        type="text"
                        value={data?.usiNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* <div className={fieldWrapperClass}>
                      <InputField
                        type="text"
                        placeholder="Bridging Visa"
                        name="bridgingVisa"
                        label="Bridging Visa"
                        value={data?.bridgingVisa ?? ""}
                        onChange={handleInputChange}
                      />
                    </div> */}
                  </div>
                </div>

                <div className={sectionClass}>
                  <h4 className={sectionTitleClass}>Emergency Contact</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-end">
                    <div className={fieldWrapperClass}>
                      <InputField
                        fullWidth
                        size="small"
                        type="text"
                        placeholder="Emergency Contact Name"
                        name="emergencyContactName"
                        label="Emergency Contact Name"
                        value={data?.emergencyContactName}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="Emergency Email"
                        name="emergencyEmail"
                        label="Emergency Email"
                        value={data?.emergencyEmail}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        fullWidth
                        size="small"
                        type="text"
                        placeholder="Emergency Contact Number"
                        name="emergencyContact"
                        label="Emergency Contact Number"
                        value={data?.emergencyContact}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={fieldWrapperClass}>
                      <InputField
                        fullWidth
                        size="small"
                        type="text"
                        placeholder="Relation With Applicant"
                        name="emergencyRelation"
                        label="Relation With Applicant"
                        value={data?.emergencyRelation}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className={sectionClass}>
                  <h4 className={sectionTitleClass}>Assignment & Referral</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
                    <div className={fieldWrapperClass}>
                      <span className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                        Case Officer *
                      </span>
                      <Autocomplete
                        options={usersList}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Case Officer"
                            helperText={
                              isExistingClient && !canAssignOfficer
                                ? "Only staff with case-officer assignment rights can change this"
                                : unlinkedOfficerName
                                ? `"${unlinkedOfficerName}" does not match a current staff member. Pick the officer to link it.`
                                : undefined
                            }
                          />
                        )}
                        getOptionLabel={(option) => option?.name}
                        size="small"
                        disabled={isExistingClient && !canAssignOfficer}
                        value={selectedOfficer}
                        onChange={(e, value) => {
                          setData((prevState) => ({ ...prevState, ...caseOfficerFields(value) }));
                        }}
                      />
                      {isExistingClient ? (
                        <Button
                          variant="text"
                          size="small"
                          sx={{ mt: 0.5 }}
                          onClick={() => setOfficerHistoryOpen(true)}
                        >
                          Case officer history
                        </Button>
                      ) : null}
                    </div>
                    {isExistingClient ? (
                      <div className={`${fieldWrapperClass} md:col-span-2`}>
                        <span className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                          Referral
                        </span>
                        <div className="flex items-center gap-3">
                          <TextField
                            size="small"
                            fullWidth
                            label="Referrer"
                            value={loadedClient?.referral || "No referrer"}
                            InputProps={{ readOnly: true }}
                            helperText={
                              loadedClient?.referralOrigin === "PORTAL"
                                ? "Came through the referral portal — only a super admin can change it"
                                : undefined
                            }
                          />
                          {canChangeReferrer(loadedClient) && (
                            <Button
                              variant="outlined"
                              onClick={() => setChangeOpen(true)}
                              className="min-w-[200px]"
                            >
                              Change referrer
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={fieldWrapperClass}>
                          <span className="text-app-text-secondary uppercase text-xs font-bold mb-2">
                            Referral
                          </span>
                          <Autocomplete
                            freeSolo
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            disabled={!canAssign}
                            options={referralOptions}
                            groupBy={(option) =>
                              option.kind === "PARTNER" ? "Referral partners" : "Staff"
                            }
                            getOptionLabel={(option) => {
                              if (typeof option === "string") return option;
                              if (option?.inputValue) return option.inputValue;
                              return option?.label || option?.name || option?.email || "";
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id || option.label}>
                                <div>
                                  <div>{option.label || option.name}</div>
                                  {option.sublabel ? (
                                    <div className="text-xs text-app-text-muted">
                                      {option.sublabel}
                                    </div>
                                  ) : null}
                                </div>
                              </li>
                            )}
                            filterOptions={(options, params) => {
                              const filtered = referralFilter(options, params);
                              const { inputValue } = params;
                              const isExisting = options.some(
                                (option) =>
                                  option?.label?.toLowerCase() === inputValue?.toLowerCase() ||
                                  option?.name?.toLowerCase() === inputValue?.toLowerCase() ||
                                  option?.email?.toLowerCase() === inputValue?.toLowerCase()
                              );
                              if (inputValue !== "" && !isExisting) {
                                filtered.push({
                                  inputValue,
                                  label: `Add "${inputValue}"`,
                                });
                              }
                              return filtered;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Referral"
                                helperText={
                                  canAssign
                                    ? undefined
                                    : "You don't have permission to set a referrer"
                                }
                              />
                            )}
                            size="small"
                            value={selectedReferralOption}
                            onChange={(e, value) => {
                              if (!canAssign) return;
                              applyReferralSelection(value);
                            }}
                            onBlur={() => {
                              if (!canAssign) return;
                              if (
                                data?.referral &&
                                !referralOptions.some(
                                  (option) =>
                                    option?.email?.toLowerCase() ===
                                      data?.referral?.toLowerCase() ||
                                    option?.label?.toLowerCase() === data?.referral?.toLowerCase()
                                )
                              ) {
                                setData((prevState) => ({
                                  ...prevState,
                                  customReferral: true,
                                }));
                              }
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id ||
                              option?.email === value?.email ||
                              option?.label === value?.label
                            }
                          />
                        </div>
                        {canAssign ? (
                          <div className="relative w-full mb-1 flex items-center">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={!!data?.customReferral}
                                  onChange={(e) =>
                                    setData((prev) => ({
                                      ...prev,
                                      customReferral: e.target.checked,
                                    }))
                                  }
                                />
                              }
                              label="Custom Referral"
                              disabled
                            />
                          </div>
                        ) : null}
                        {data?.customReferral && (
                          <div className={`${fieldWrapperClass} md:col-span-2`}>
                            <InputField
                              fullWidth
                              size="small"
                              type="text"
                              placeholder="Referral"
                              name="referral"
                              label="Custom Referral Name/Email"
                              value={data?.referral ?? data?.reference}
                              onChange={handleInputChange}
                              disabled
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="w-full flex justify-end mt-2 gap-4 border-t border-app-border pt-5">
                  <Button
                    variant="outlined"
                    onClick={() => (importedData ? setOpenEditModal(false) : navigate(-1))}
                  >
                    Go Back
                  </Button>
                  <Button variant="contained" type="submit">
                    Submit
                  </Button>
                </div>
              </form>
              {isExistingClient && (
                <ReferrerChangeDialog
                  open={changeOpen}
                  client={loadedClient}
                  users={orgUsers || []}
                  onClose={() => setChangeOpen(false)}
                  onChanged={(result) => {
                    setChangeOpen(false);
                    const changed = {
                      referral: result.newLabel,
                      referralPartnerId: result.newPartnerId,
                      customReferral: !result.newPartnerId && !!result.newLabel,
                    };
                    setLoadedClient((prev) => ({ ...prev, ...changed }));
                    setData((prev) => ({ ...prev, ...changed }));
                  }}
                />
              )}
              {isExistingClient && (
                <CaseOfficerHistoryDialog
                  open={officerHistoryOpen}
                  clientId={loadedClient?.id || data?.id}
                  onClose={() => setOfficerHistoryOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>{" "}
      {isLoading && <WholeScreenLoader open={isLoading} />}
    </div>
  );
};

export default AddClient;
